<template>
    <!-- Each workflow screen owns its navigation header, including Back and screen-specific actions. -->
    <BaseDialog v-model:open="isOpen" :title="t('feedback.creation.dialogTitle')" layout="custom" dialog-content-class="overflow-y-hidden" data-testid="feedback-modal">
        <!-- List View -->
        <FeedbackModalContainer v-if="!isInCreationMode && !isInSharingMode && showListView">
            <FeedbackModalHeader :title="t('feedback.creation.listTitle')" :sub-title="t('feedback.creation.listSubtitle')" @close="handleClose">
                <template #actions>
                    <BaseButton theme="primary" @click="goToCreationMode">
                        <div class="flex items-center gap-2">
                            <i class="bi bi-plus-lg font-bold" aria-hidden="true"></i>
                            <span class="body-small">{{ t("feedback.creation.newRequest") }}</span>
                        </div>
                    </BaseButton>
                </template>
            </FeedbackModalHeader>
            <FeedbackModalBody>
                <div class="flex flex-col justify-start gap-5 w-full h-full">
                    <!-- Three Steps Overview -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <FeedbackStepCard icon="bi-pencil" :title="t('feedback.creation.steps.craftTitle')" :description="t('feedback.creation.steps.craftDescription')" />
                        <FeedbackStepCard icon="bi-send" :title="t('feedback.creation.steps.collectTitle')" :description="t('feedback.creation.steps.collectDescription')" />
                        <FeedbackStepCard
                            icon="bi-rocket"
                            :title="t('feedback.creation.steps.reviewTitle')"
                            :description="t('feedback.creation.steps.reviewDescription', { count: minimumResponseCount }, minimumResponseCount)" />
                    </div>

                    <!-- Existing Requests Heading -->
                    <h2 v-if="feedbackQuestions && feedbackQuestions.length > 0" class="title-body text-ink mb-4">{{ t("feedback.creation.existingRequestsHeading") }}</h2>

                    <FeedbackRequestCard
                        v-for="feedback in feedbackQuestions"
                        :key="feedback.id"
                        :feedback="feedback"
                        :button-text="resolvedButtonText"
                        :url-button-text="resolvedUrlButtonText"
                        :minimum-response-count="minimumResponseCount"
                        @delete="(id) => $emit('delete', id)"
                        @copy-link="(id) => $emit('copy-link', id, 'list')"
                        @copy-url="(id) => $emit('copy-url', id, 'list')"
                        @close-and-review="(feedback) => $emit('close-and-review', feedback)"
                        @view-report="(feedback) => $emit('view-report', feedback)"
                        @reopen="(feedback) => $emit('reopen', feedback)" />
                </div>
            </FeedbackModalBody>
        </FeedbackModalContainer>

        <!-- Creation View -->
        <FeedbackModalContainer v-else-if="isInCreationMode && !isInSharingMode">
            <FeedbackModalHeader :title="t('feedback.modal.defaultTitle')" :show-back-button="showListView" @close="handleClose" @back="goBackToList" />

            <FeedbackModalBody>
                <!-- Essential Questions -->
                <template v-for="template in questionTemplates" :key="template.name">
                    <div class="mb-10">
                        <h3 class="body-small text-ink-muted mb-4">{{ template.name }}</h3>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <BaseCard
                                v-for="question in template.questions"
                                :key="question"
                                as="button"
                                type="button"
                                padding="lg"
                                radius="lgr"
                                data-testid="feedback-template-question"
                                @click="createFromTemplate(question)">
                                <p class="text-ink title-body">{{ question }}</p>
                            </BaseCard>
                        </div>
                    </div>
                </template>

                <!-- Creation Options -->
                <div class="mb-6">
                    <h3 class="body-small text-ink-muted mb-4">{{ t("feedback.creation.customQuestionHeading") }}</h3>

                    <!-- Action Buttons -->
                    <div v-if="!showManualInput" class="flex flex-col sm:flex-row gap-3 mb-6">
                        <!-- Create with Nadia - Preferred Option -->
                        <BaseButton theme="primary" class="flex-1" @click="goToChatCreation">
                            <div class="flex items-center gap-2 whitespace-nowrap justify-center">
                                <div class="w-5 h-5">
                                    <CoachingModeMarble size="small" />
                                </div>
                                {{ t("feedback.creation.createWithNadia") }}
                            </div>
                        </BaseButton>

                        <!-- Manual Creation -->
                        <BaseButton theme="secondary" class="flex-1" @click="showManualInput = true"> {{ t("feedback.creation.createManually") }} </BaseButton>
                    </div>

                    <!-- Manual Input (conditionally shown) -->
                    <div v-if="showManualInput" class="border-2 border-border rounded-lgr p-4 focus-field-within">
                        <BaseTextarea
                            v-model="customQuestion"
                            :aria-label="t('feedback.creation.customQuestionLabel')"
                            :placeholder="t('feedback.creation.customQuestionPlaceholder')"
                            class="w-full min-h-25 resize-none border-none focus:outline-hidden text-ink placeholder-control-placeholder"
                            :class="{ 'border-notification-error-border': customQuestionError }"
                            :maxlength="MAX_QUESTION_LENGTH"></BaseTextarea>

                        <div class="flex justify-between items-center mt-4 pt-4 border-t border-border">
                            <div class="flex items-center gap-3">
                                <BaseButton
                                    theme="neutral"
                                    @click="
                                        showManualInput = false;
                                        customQuestion = '';
                                        customQuestionError = '';
                                    ">
                                    {{ t("common.cancel") }}
                                </BaseButton>
                            </div>
                            <div class="flex items-center gap-4">
                                <span v-if="customQuestionError" role="alert" class="text-notification-error-text body-small">{{ customQuestionError }}</span>
                                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- numeric counter, no translatable text -->
                                <span class="text-ink-muted body-small">{{ customQuestion.length }}/{{ MAX_QUESTION_LENGTH }}</span>
                                <BaseButton
                                    theme="primary"
                                    :disabled="!customQuestion.trim() || customQuestion.length < MIN_QUESTION_LENGTH || isCreating"
                                    @click="createFromCustom">
                                    {{ isCreating ? t("feedback.creation.creatingButton") : t("feedback.creation.createButton") }}
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </div>
            </FeedbackModalBody>

            <!-- Loading State -->
            <div v-if="isCreating" class="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                <div class="bg-surface-raised rounded-lg p-6 flex flex-col items-center gap-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-control-accent"></div>
                    <span class="text-ink">{{ t("feedback.creation.creatingStatus") }}</span>
                </div>
            </div>
        </FeedbackModalContainer>

        <!-- Sharing View -->
        <FeedbackModalContainer v-else-if="isInSharingMode">
            <FeedbackModalHeader :title="t('feedback.modal.defaultTitle')" :show-back-button="showListView" @close="handleClose" @back="goBackToList" />

            <FeedbackModalBody>
                <!-- Feedback Request Card -->
                <div class="border-2 border-control-border-focus rounded-lg p-6 mb-10">
                    <div class="flex justify-between items-start mb-4">
                        <h3 class="text-ink-muted body-semibold">{{ t("feedback.creation.sharing.heading") }}</h3>
                    </div>

                    <div class="mb-6">
                        <h4 class="title-body text-ink mb-4">{{ newlyCreatedQuestion?.question }}</h4>
                    </div>

                    <div class="grid justify-items-end">
                        <div class="flex gap-2">
                            <BaseButton data-testid="feedback-copy-link-button" theme="tertiary" @click="$emit('copy-link', newlyCreatedQuestion?.id, 'post_creation')">
                                {{ resolvedButtonText }}
                            </BaseButton>
                            <BaseButton data-testid="feedback-copy-url-button" theme="tertiary" @click="$emit('copy-url', newlyCreatedQuestion?.id, 'post_creation')">
                                {{ resolvedUrlButtonText }}
                            </BaseButton>
                        </div>
                    </div>
                </div>

                <!-- What's Next Section -->
                <h2 class="title-body text-ink mb-4">{{ t("feedback.creation.sharing.whatsNextHeading") }}</h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FeedbackStepCard icon="bi-share" :title="t('feedback.creation.sharing.shareTitle')" :description="t('feedback.creation.sharing.shareDescription')" />
                    <FeedbackStepCard icon="bi-envelope" :title="t('feedback.creation.sharing.notifyTitle')" :description="t('feedback.creation.sharing.notifyDescription')" />
                </div>
            </FeedbackModalBody>
        </FeedbackModalContainer>
    </BaseDialog>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import FeedbackModalBody from "~vue/Feedback/FeedbackModalBody.vue";
import FeedbackModalContainer from "~vue/Feedback/FeedbackModalContainer.vue";
import FeedbackModalHeader from "~vue/Feedback/FeedbackModalHeader.vue";
import FeedbackRequestCard from "~vue/Feedback/FeedbackRequestCard.vue";
import FeedbackStepCard from "~vue/Feedback/FeedbackStepCard.vue";
import { openUrl } from "~vue/utils";
import { CHATSOURCE } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    open: { type: Boolean, default: false },
    showListView: { type: Boolean, default: true },
    feedbackQuestions: { type: Array, default: () => [] },
    buttonText: { type: String, default: null },
    urlButtonText: { type: String, default: null },
    minimumResponseCount: { type: Number, default: 3 },
});

const emit = defineEmits(["update:open", "delete", "copy-link", "copy-url", "close-and-review", "view-report", "reopen", "question-created"]);

const { t } = useI18n();

const coachingSessionUuid = inject("coachingSessionUuid", null);

const resolvedButtonText = computed(() => props.buttonText ?? t("feedback.request.copyInvite"));
const resolvedUrlButtonText = computed(() => props.urlButtonText ?? t("feedback.request.copyUrl"));

const MIN_QUESTION_LENGTH = 10;
const MAX_QUESTION_LENGTH = 500;

const isOpen = computed({
    get: () => props.open,
    set: (value) => emit("update:open", value),
});

// Modal state management
const isInCreationMode = ref(false);
const isInSharingMode = ref(false);
const customQuestion = ref("");
const customQuestionError = ref("");
const isCreating = ref(false);
const showManualInput = ref(false);
const newlyCreatedQuestion = ref(null);

// The chosen template text is saved as the question, so it is stored in the requester's interface language.
const questionTemplates = computed(() => [
    {
        name: t("feedback.creation.templates.essential.name"),
        questions: [
            t("feedback.creation.templates.essential.communication"),
            t("feedback.creation.templates.essential.collaboration"),
            t("feedback.creation.templates.essential.approachability"),
            t("feedback.creation.templates.essential.roleSkills"),
        ],
    },
    {
        name: t("feedback.creation.templates.thoughtProvoking.name"),
        questions: [
            t("feedback.creation.templates.thoughtProvoking.accountability"),
            t("feedback.creation.templates.thoughtProvoking.inclusion"),
            t("feedback.creation.templates.thoughtProvoking.adaptability"),
            t("feedback.creation.templates.thoughtProvoking.priorities"),
        ],
    },
]);

// Modal management functions
const goToCreationMode = () => {
    logUserInteraction("feedback_gathering", {}, coachingSessionUuid);
    isInCreationMode.value = true;
};

const resetModalState = () => {
    isInCreationMode.value = false;
    isInSharingMode.value = false;
    customQuestion.value = "";
    customQuestionError.value = "";
    newlyCreatedQuestion.value = null;
    isCreating.value = false;
    showManualInput.value = false;
};

const goBackToList = () => {
    resetModalState();
};

const handleClose = () => {
    isOpen.value = false;
    resetModalState();
};

const goToSharingMode = (question) => {
    isInCreationMode.value = false;
    isInSharingMode.value = true;
    newlyCreatedQuestion.value = question;
};

const createFromTemplate = async (questionText) => {
    await createFeedbackQuestion(questionText, "template");
};

const createFromCustom = async () => {
    if (!customQuestion.value.trim()) {
        customQuestionError.value = t("feedback.creation.emptyQuestionError");
        return;
    }

    if (customQuestion.value.length < MIN_QUESTION_LENGTH) {
        customQuestionError.value = t("feedback.creation.shortQuestionError", { min: MIN_QUESTION_LENGTH });
        return;
    }

    customQuestionError.value = "";
    await createFeedbackQuestion(customQuestion.value.trim(), "manual");
};

const createFeedbackQuestion = async (questionText, creationMethod) => {
    isCreating.value = true;
    customQuestionError.value = "";

    try {
        logUserInteraction(
            "feedback_question_created",
            {
                creation_method: creationMethod,
                question_length: questionText.length,
            },
            coachingSessionUuid,
        );

        // Create a new useFetch instance for this specific request
        const { execute, data, statusCode } = useFetch("/api/accounts/feedback-questions", { immediate: false })
            .post({
                question: questionText,
                creation_method: creationMethod,
            })
            .json();

        await execute();

        if (statusCode.value === 400 && data.value?.question) {
            customQuestionError.value = Array.isArray(data.value.question) ? data.value.question[0] : data.value.question;
            return;
        }

        // Notify parent that a question was created
        emit("question-created", data.value);

        // Go to sharing view
        goToSharingMode(data.value);
    } catch (error) {
        console.error("Error creating feedback question:", error);
    } finally {
        isCreating.value = false;
    }
};

const goToChatCreation = () => {
    logUserInteraction("feedback_chat_selected", {}, coachingSessionUuid);
    openUrl("coach/start/?script=feedback_create_request", {
        queryParams: { chat_source: CHATSOURCE.HOME_USER_FEEDBACK },
    });
};

// Watch for modal open to set initial state
watch(
    () => props.open,
    (newValue) => {
        if (newValue) {
            // If not showing list view, go directly to creation mode
            if (!props.showListView) {
                isInCreationMode.value = true;
            }
        } else {
            // Modal was closed, reset all state
            resetModalState();
        }
    },
);
</script>
